import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Fade,
  Stack,
  Autocomplete,
  ListSubheader,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ShoppingCart as CartIcon,
  LocationOn as LocationIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as ShippingIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Money as CashIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  Stars as StarsIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLocation as useUserLocation } from '../contexts/LocationContext';
import { convertEnglishToBengali, parsePrice, formatPriceWithBengaliDigits } from '../utils/priceUtils';
import checkoutBn from '../i18n/bn/checkout';
import checkoutEn from '../i18n/en/checkout';
import checkoutNp from '../i18n/np/checkout';
import checkoutPk from '../i18n/pk/checkout';
import checkoutBt from '../i18n/bt/checkout';
import checkoutDe from '../i18n/de/checkout';
import checkoutSl from '../i18n/sl/checkout';
import checkoutAf from '../i18n/af/checkout';
import checkoutCn from '../i18n/cn/checkout';

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.05)',
  overflow: 'hidden',
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 15px 50px rgba(0,0,0,0.12)',
  }
}));

const GradientButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1.1rem',
  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 35px rgba(0,0,0,0.2)',
    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
  }
}));

const StepCard = styled(Card)(({ theme, active }) => ({
  borderRadius: theme.spacing(2),
  border: active ? '2px solid #667eea' : '2px solid transparent',
  background: active 
    ? 'linear-gradient(145deg, #667eea10 0%, #764ba210 100%)' 
    : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
  }
}));

const PaymentMethodCard = styled(Card)(({ theme, selected }) => ({
  borderRadius: theme.spacing(2),
  border: selected ? '2px solid #667eea' : '2px solid #e0e0e0',
  background: selected 
    ? 'linear-gradient(145deg, #667eea15 0%, #764ba215 100%)' 
    : 'white',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
    borderColor: '#667eea',
  }
}));

const OrderSummaryCard = styled(StyledPaper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  background: 'linear-gradient(145deg, #f8f9fa 0%, #e3f2fd 100%)',
  border: '1px solid #bbdefb',
}));

// Language-specific texts will be computed inside the component so they update on language change

const districts = [
  'ঢাকা', 'চট্টগ্রাম', 'রাজশাহী', 'খুলনা', 'বরিশাল', 'সিলেট', 'রংপুর', 'ময়মনসিংহ',
  'কুমিল্লা', 'নোয়াখালী', 'ফেনী', 'লক্ষ্মীপুর', 'ব্রাহ্মণবাড়িয়া', 'চাঁদপুর', 'কিশোরগঞ্জ',
  'মানিকগঞ্জ', 'গাজীপুর', 'নরসিংদী', 'টাঙ্গাইল', 'ফরিদপুর', 'গোপালগঞ্জ', 'মাদারীপুর',
  'শরীয়তপুর', 'রাজবাড়ী', 'নারায়ণগঞ্জ', 'মুন্শিগঞ্জ', 'নাটোর', 'নওগাঁ', 'পাবনা',
  'বগুড়া', 'জয়পুরহাট', 'চাপাইনবাবগঞ্জ', 'সিরাজগঞ্জ', 'যশোর', 'সাতক্ষীরা', 'মেহেরপুর',
  'নড়াইল', 'চুয়াডাঙ্গা', 'কুষ্টিয়া', 'মাগুরা', 'ঝিনাইদহ', 'বাগেরহাট', 'পিরোজপুর',
  'ঝালকাঠি', 'পটুয়াখালী', 'বরগুনা', 'ভোলা', 'হবিগঞ্জ', 'মৌলভীবাজার', 'সুনামগঞ্জ',
  'দিনাজপুর', 'ঠাকুরগাঁও', 'পঞ্চগড়', 'নীলফামারী', 'লালমনিরহাট', 'কুড়িগ্রাম', 'গাইবান্ধা',
  'জামালপুর', 'শেরপুর', 'নেত্রকোণা', 'বান্দরবান', 'রাঙ্গামাটি', 'খাগড়াছড়ি', 'কক্সবাজার'
];

// Payment methods depend on language; will be defined inside component

const Checkout = () => {
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState((typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn');
  const t = currentLanguage === 'en' ? checkoutEn : 
            currentLanguage === 'np' ? checkoutNp : 
            currentLanguage === 'pk' ? checkoutPk : 
            currentLanguage === 'bt' ? checkoutBt : 
            currentLanguage === 'de' ? checkoutDe : 
            currentLanguage === 'sl' ? checkoutSl : 
            currentLanguage === 'af' ? checkoutAf : 
            currentLanguage === 'cn' ? checkoutCn : 
            checkoutBn;
  const tCheckout = t; // keep existing references working
  const steps = t.steps;
  const paymentMethods = [
    { id: 'cash', name: t.paymentMethods.cash.name, description: t.paymentMethods.cash.desc, icon: <CashIcon />, fee: t.paymentMethods.cash.fee },
    { id: 'bkash', name: t.paymentMethods.bkash.name, description: t.paymentMethods.bkash.desc, icon: <PhoneIcon />, fee: t.paymentMethods.bkash.fee },
    { id: 'nagad', name: t.paymentMethods.nagad.name, description: t.paymentMethods.nagad.desc, icon: <PhoneIcon />, fee: t.paymentMethods.nagad.fee },
    { id: 'rocket', name: t.paymentMethods.rocket.name, description: t.paymentMethods.rocket.desc, icon: <PhoneIcon />, fee: t.paymentMethods.rocket.fee },
    { id: 'card', name: t.paymentMethods.card.name, description: t.paymentMethods.card.desc, icon: <CreditCardIcon />, fee: t.paymentMethods.card.fee },
    { id: 'bank', name: t.paymentMethods.bank.name, description: t.paymentMethods.bank.desc, icon: <BankIcon />, fee: t.paymentMethods.bank.fee }
  ];
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { userLocation } = useUserLocation();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState('');
  
  // Form states
  const [shippingInfo, setShippingInfo] = useState({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    district: '',
    postalCode: '',
    deliveryInstructions: ''
  });
  
  const [paymentInfo, setPaymentInfo] = useState({
    method: 'cash',
    mobileNumber: '',
    transactionId: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardHolderName: ''
  });
  
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // React to language changes (dispatched by Header)
  useEffect(() => {
    const updateLang = () => {
      const lang = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
      setCurrentLanguage(lang);
    };
    const storageHandler = (e) => {
      if (e.key === 'preferredLanguage') updateLang();
    };
    window.addEventListener('preferredLanguageChanged', updateLang);
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('preferredLanguageChanged', updateLang);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  // Prefill district and upazila (city) from saved user location
  useEffect(() => {
    if (userLocation) {
      setShippingInfo(prev => ({
        ...prev,
        district: prev.district || userLocation.district || '',
        city: prev.city || userLocation.upazilla || ''
      }));
    }
  }, [userLocation]);

  // Redirect if cart is empty
  useEffect(() => {
    // Temporarily commented out for design inspection
    // if (!isAuthenticated) {
    //   navigate('/auth');
    //   return;
    // }
    
    if (cartItems.length === 0) {
      navigate('/cart');
      return;
    }
  }, [cartItems, isAuthenticated, navigate]);

  // Prevent page drift when dropdowns open
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      html {
        overflow-y: scroll !important;
        scrollbar-gutter: stable;
      }
      body {
        overflow-x: hidden;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Calculate totals
  const subtotal = getCartTotal();
  const deliveryFee = subtotal > 1000 ? 0 : 60;
  const selectedPaymentMethod = paymentMethods.find(method => method.id === paymentInfo.method);
  const paymentFee = selectedPaymentMethod?.fee || 0;
  const total = subtotal + deliveryFee + paymentFee;

  // Handle form changes
  const handleShippingChange = (field, value) => {
    setShippingInfo(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePaymentChange = (field, value) => {
    setPaymentInfo(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validation functions
  const validateShippingInfo = () => {
    const newErrors = {};
    
    if (!shippingInfo.fullName.trim()) {
      newErrors.fullName = tCheckout.shipping.errors.fullName;
    }
    
    if (!shippingInfo.email.trim()) {
      newErrors.email = tCheckout.shipping.errors.email;
    } else if (!/\S+@\S+\.\S+/.test(shippingInfo.email)) {
      newErrors.email = tCheckout.shipping.errors.email;
    }
    
    if (!shippingInfo.phone.trim()) {
      newErrors.phone = tCheckout.shipping.errors.phone;
    } else if (!/^(\+88)?01[3-9]\d{8}$/.test(shippingInfo.phone.replace(/\s/g, ''))) {
      newErrors.phone = tCheckout.shipping.errors.phone;
    }
    
    if (!shippingInfo.address.trim()) {
      newErrors.address = tCheckout.shipping.errors.address;
    }
    
    if (!shippingInfo.city.trim()) {
      newErrors.city = tCheckout.shipping.errors.city;
    }
    
    if (!shippingInfo.district) {
      newErrors.district = tCheckout.shipping.errors.district;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePaymentInfo = () => {
    const newErrors = {};
    
    if (paymentInfo.method === 'bkash' || paymentInfo.method === 'nagad' || paymentInfo.method === 'rocket') {
      if (!paymentInfo.mobileNumber.trim()) {
        newErrors.mobileNumber = tCheckout.shipping.errors.phone;
      }
    }
    
    if (paymentInfo.method === 'card') {
      if (!paymentInfo.cardNumber.trim()) {
        newErrors.cardNumber = tCheckout.payment.cardNumber;
      }
      if (!paymentInfo.expiryDate.trim()) {
        newErrors.expiryDate = tCheckout.payment.expiry;
      }
      if (!paymentInfo.cvv.trim()) {
        newErrors.cvv = tCheckout.payment.cvv;
      }
      if (!paymentInfo.cardHolderName.trim()) {
        newErrors.cardHolderName = tCheckout.payment.cardHolder;
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step navigation
  const handleNext = () => {
    if (activeStep === 1 && !validateShippingInfo()) {
      return;
    }
    
    if (activeStep === 2 && !validatePaymentInfo()) {
      return;
    }
    
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // Place order
  const handlePlaceOrder = async () => {
    setLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate order ID
      const newOrderId = 'GB' + Date.now().toString().slice(-8);
      setOrderId(newOrderId);
      
      // Clear cart
      clearCart();
      
      // Set order placed
      setOrderPlaced(true);
      setActiveStep(3);
      
      setSnackbar({
        open: true,
        message: currentLanguage === 'en' ? 'Order placed successfully!' : 'অর্ডার সফলভাবে সম্পন্ন হয়েছে!',
        severity: 'success'
      });
      
    } catch (error) {
      setSnackbar({
        open: true,
        message: currentLanguage === 'en' ? 'There was a problem processing the order. Please try again.' : 'অর্ডার প্রক্রিয়াকরণে সমস্যা হয়েছে। আবার চেষ্টা করুন।',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Render step content
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StyledPaper sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <CartIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {tCheckout.order.details}
              </Typography>
            </Box>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {tCheckout.order.verify}
            </Typography>

            <List sx={{ mb: 4 }}>
              {cartItems.map((item, index) => (
                <Card key={item.id} sx={{ mb: 2, borderRadius: 3, border: '1px solid #e0e0e0' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Avatar
                        src={item.image}
                        alt={item.name}
                        variant="rounded"
                        sx={{ width: 80, height: 80, border: '2px solid #f0f0f0' }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {currentLanguage === 'en' ? '🏪 Store: ' : '🏪 দোকান: '}{item.store}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {currentLanguage === 'en' ? '📦 Qty: ' : '📦 পরিমাণ: '}{convertEnglishToBengali(item.quantity.toString())}{currentLanguage === 'en' ? '' : ' টি'}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {currentLanguage === 'en' ? 'Unit price: ' : 'একক দাম: '}{formatPriceWithBengaliDigits(item.price)}
                        </Typography>
                        <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                          {formatPriceWithBengaliDigits(parsePrice(item.price) * item.quantity)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </List>
            
            <Card sx={{ background: 'linear-gradient(145deg, #f8f9fa 0%, #e3f2fd 100%)', border: '1px solid #bbdefb', borderRadius: 3, mb: 4 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">{tCheckout.order.subtotal}:</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatPriceWithBengaliDigits(subtotal)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">{tCheckout.order.delivery}:</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: deliveryFee === 0 ? 'success.main' : 'text.primary' }}>
                      {deliveryFee === 0 ? tCheckout.order.free : formatPriceWithBengaliDigits(deliveryFee)}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{tCheckout.order.total}:</Typography>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 800 }}>
                      {formatPriceWithBengaliDigits(subtotal + deliveryFee)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
            
              <GradientButton
              variant="contained"
              fullWidth
              onClick={handleNext}
              size="large"
              endIcon={<LocationIcon />}
            >
                {tCheckout.order.goToShipping}
            </GradientButton>
          </StyledPaper>
        );

      case 1:
        return (
          <StyledPaper sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <LocationIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {tCheckout.shipping.title}
              </Typography>
            </Box>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {tCheckout.shipping.desc}
            </Typography>
            
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tCheckout.shipping.fullName}
                  value={shippingInfo.fullName}
                  onChange={(e) => handleShippingChange('fullName', e.target.value)}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tCheckout.shipping.email}
                  type="email"
                  value={shippingInfo.email}
                  onChange={(e) => handleShippingChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tCheckout.shipping.phone}
                  value={shippingInfo.phone}
                  onChange={(e) => handleShippingChange('phone', e.target.value)}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  placeholder={tCheckout.payment.labels.mobilePh}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  value={shippingInfo.district || null}
                  onChange={(event, newValue) => {
                    handleShippingChange('district', newValue || '');
                  }}
                  options={districts}
                  getOptionLabel={(option) => option}
                  isOptionEqualToValue={(option, value) => option === value}
                  disablePortal={false}
                  disableScrollLock={true}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={tCheckout.shipping.district}
                      error={!!errors.district}
                      helperText={errors.district}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          }
                        }
                      }}
                    />
                  )}
                  ListboxProps={{
                    sx: {
                      maxHeight: 300,
                      '& .MuiAutocomplete-option': {
                        fontSize: '1rem',
                        padding: '12px 16px',
                        '&:hover': {
                          backgroundColor: 'primary.50',
                        },
                        '&[aria-selected="true"]': {
                          backgroundColor: 'primary.100',
                          '&:hover': {
                            backgroundColor: 'primary.150',
                          }
                        }
                      },
                      // Hide scrollbar but keep scrolling functionality
                      scrollbarWidth: 'none', // Firefox
                      msOverflowStyle: 'none', // IE and Edge
                      '&::-webkit-scrollbar': {
                        display: 'none', // Chrome, Safari and Opera
                      },
                    }
                  }}
                  PaperComponent={({ children, ...other }) => (
                    <Paper
                      {...other}
                      sx={{
                        borderRadius: 2,
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                        border: '1px solid rgba(0,0,0,0.05)',
                        mt: 1,
                        '& .MuiAutocomplete-listbox': {
                          padding: 0,
                        }
                      }}
                    >
                      {children}
                    </Paper>
                  )}
                   noOptionsText={tCheckout.shipping.noDistrict}
                   placeholder={tCheckout.shipping.searchDistrict}
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      paddingLeft: '14px !important',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tCheckout.shipping.city}
                  value={shippingInfo.city}
                  onChange={(e) => handleShippingChange('city', e.target.value)}
                  error={!!errors.city}
                  helperText={errors.city}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={tCheckout.shipping.postal}
                  value={shippingInfo.postalCode}
                  onChange={(e) => handleShippingChange('postalCode', e.target.value)}
                  placeholder="১২০০"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={tCheckout.shipping.address}
                  multiline
                  rows={3}
                  value={shippingInfo.address}
                  onChange={(e) => handleShippingChange('address', e.target.value)}
                  error={!!errors.address}
                  helperText={errors.address}
                  placeholder={tCheckout.shipping.addressPh}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={tCheckout.shipping.instruction}
                  multiline
                  rows={2}
                  value={shippingInfo.deliveryInstructions}
                  onChange={(e) => handleShippingChange('deliveryInstructions', e.target.value)}
                  placeholder={tCheckout.shipping.instructionPh}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
            
            <Alert severity="info" sx={{ mt: 4, borderRadius: 2 }}>
              {tCheckout.shipping.notice}
            </Alert>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
              <Button
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1.1rem'
                }}
              >
                {tCheckout.shipping.prev}
              </Button>
              <GradientButton
                variant="contained"
                onClick={handleNext}
                size="large"
                endIcon={<PaymentIcon />}
              >
                {tCheckout.shipping.goToPayment}
              </GradientButton>
            </Box>
          </StyledPaper>
        );

      case 2:
        return (
          <StyledPaper sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <PaymentIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {tCheckout.payment.title}
              </Typography>
            </Box>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {tCheckout.payment.desc}
            </Typography>
            
            <FormControl component="fieldset" fullWidth>
              <Grid container spacing={3}>
                {paymentMethods.map((method) => (
                  <Grid item xs={12} sm={6} md={4} key={method.id}>
                    <PaymentMethodCard 
                      selected={paymentInfo.method === method.id}
                      onClick={() => handlePaymentChange('method', method.id)}
                    >
                      <CardContent sx={{ p: 3, textAlign: 'center' }}>
                        <Box sx={{ mb: 2, color: paymentInfo.method === method.id ? 'primary.main' : 'text.secondary' }}>
                          {React.cloneElement(method.icon, { sx: { fontSize: 40 } })}
                        </Box>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600, 
                            mb: 1,
                            color: paymentInfo.method === method.id ? 'primary.main' : 'text.primary'
                          }}
                        >
                          {method.name}
                          {method.fee > 0 && (
                            <Chip
                              label={`+${formatPriceWithBengaliDigits(method.fee)}`}
                              size="small"
                              color="warning"
                              sx={{ ml: 1, fontSize: '0.75rem' }}
                            />
                          )}
                          {method.id === 'cash' && (
                            <Chip
                              label={tCheckout.payment.recommended}
                              size="small"
                              color="success"
                              sx={{ ml: 1, fontSize: '0.75rem' }}
                            />
                          )}
                        </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            {method.description}
                        </Typography>
                        {paymentInfo.method === method.id && (
                          <Box sx={{ mt: 2 }}>
                            <CheckCircleIcon color="primary" />
                          </Box>
                        )}
                      </CardContent>
                    </PaymentMethodCard>
                  </Grid>
                ))}
              </Grid>
            </FormControl>
            
            {/* Additional fields for specific payment methods */}
            {paymentInfo.method && paymentInfo.method !== 'cash' && (
              <Fade in timeout={500}>
                <Card sx={{ mt: 4, border: '2px solid', borderColor: 'primary.light', borderRadius: 3 }}>
                  <CardContent sx={{ p: 4 }}>
                     <Typography variant="h6" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
                       {tCheckout.payment.info}
                    </Typography>
                    
                    {(paymentInfo.method === 'bkash' || paymentInfo.method === 'nagad' || paymentInfo.method === 'rocket') && (
                       <TextField
                        fullWidth
                         label={tCheckout.payment.mobile}
                        value={paymentInfo.mobileNumber}
                        onChange={(e) => handlePaymentChange('mobileNumber', e.target.value)}
                        error={!!errors.mobileNumber}
                         helperText={errors.mobileNumber}
                         placeholder={tCheckout.payment.labels.mobilePh}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                            }
                          }
                        }}
                      />
                    )}
                    
                    {paymentInfo.method === 'card' && (
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                           <TextField
                            fullWidth
                             label={tCheckout.payment.cardNumber}
                            value={paymentInfo.cardNumber}
                            onChange={(e) => handlePaymentChange('cardNumber', e.target.value)}
                            error={!!errors.cardNumber}
                             helperText={errors.cardNumber}
                             placeholder={tCheckout.payment.labels.cardNumberPh}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                           <TextField
                            fullWidth
                             label={tCheckout.payment.cardHolder}
                            value={paymentInfo.cardHolderName}
                            onChange={(e) => handlePaymentChange('cardHolderName', e.target.value)}
                            error={!!errors.cardHolderName}
                            helperText={errors.cardHolderName}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                           <TextField
                            fullWidth
                             label={tCheckout.payment.expiry}
                            value={paymentInfo.expiryDate}
                            onChange={(e) => handlePaymentChange('expiryDate', e.target.value)}
                            error={!!errors.expiryDate}
                             helperText={errors.expiryDate}
                             placeholder={tCheckout.payment.labels.expiryPh}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                           <TextField
                            fullWidth
                             label={tCheckout.payment.cvv}
                            value={paymentInfo.cvv}
                            onChange={(e) => handlePaymentChange('cvv', e.target.value)}
                            error={!!errors.cvv}
                             helperText={errors.cvv}
                             placeholder={tCheckout.payment.labels.cvvPh}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                }
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              </Fade>
            )}
            
            {paymentInfo.method === 'cash' && (
              <Alert 
                severity="success" 
                sx={{ 
                  mt: 4, 
                  borderRadius: 2,
                  background: 'linear-gradient(145deg, #e8f5e8 0%, #c8e6c9 100%)',
                  border: '1px solid #4caf50'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CashIcon sx={{ mr: 1, fontSize: 24 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.dark' }}>
                      {tCheckout.payment.cashSelectedTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'success.dark' }}>
                      {tCheckout.payment.cashSelectedDesc}
                    </Typography>
                  </Box>
                </Box>
              </Alert>
            )}
            
            <Divider sx={{ my: 4, borderColor: 'primary.light' }} />
            
            {/* Order Summary */}
            <Card sx={{ background: 'linear-gradient(145deg, #f8f9fa 0%, #e3f2fd 100%)', border: '1px solid #bbdefb', borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: 'primary.main', fontWeight: 700 }}>
                  {tCheckout.payment.orderSummary}
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">{tCheckout.order.subtotal}:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatPriceWithBengaliDigits(subtotal)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">{tCheckout.order.delivery}:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: deliveryFee === 0 ? 'success.main' : 'text.primary' }}>
                      {deliveryFee === 0 ? tCheckout.order.free : formatPriceWithBengaliDigits(deliveryFee)}
                    </Typography>
                  </Box>
                  {paymentFee > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">{tCheckout.payment.paymentFee}:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatPriceWithBengaliDigits(paymentFee)}
                      </Typography>
                    </Box>
                  )}
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{tCheckout.order.total}:</Typography>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 800 }}>
                      {formatPriceWithBengaliDigits(total)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
              <Button
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1.1rem'
                }}
              >
                {tCheckout.payment.prev}
              </Button>
              <GradientButton
                variant="contained"
                onClick={handlePlaceOrder}
                disabled={loading}
                size="large"
                endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              >
                {loading ? tCheckout.payment.processing : tCheckout.payment.confirm}
              </GradientButton>
            </Box>
          </StyledPaper>
        );

      case 3:
        return (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              {tCheckout.success.title}
            </Typography>
            <Typography variant="h6" gutterBottom>
              {tCheckout.success.orderNo(orderId)}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {tCheckout.success.desc}
            </Typography>
            
            <Box sx={{ mt: 4, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {tCheckout.success.deliveryInfo}
              </Typography>
              <Card sx={{ maxWidth: 400, mx: 'auto' }}>
                <CardContent>
                  <Typography variant="body2" gutterBottom>
                    <strong>{tCheckout.success.fields.name}:</strong> {shippingInfo.fullName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>{tCheckout.success.fields.phone}:</strong> {shippingInfo.phone}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>{tCheckout.success.fields.address}:</strong> {shippingInfo.address}, {shippingInfo.city}, {shippingInfo.district}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>{tCheckout.success.fields.payment}:</strong> {selectedPaymentMethod?.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{tCheckout.success.fields.total}:</strong> ৳{total.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                component={Link}
                to="/customer/dashboard"
                sx={{
                  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)'
                }}
              >
                {tCheckout.success.track}
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to="/"
              >
                {tCheckout.success.home}
              </Button>
            </Box>
          </Paper>
        );

      default:
        return null;
    }
  };

  if (cartItems.length === 0 && !orderPlaced) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ my: 4, minHeight: '60vh' }}>
          <Alert severity="warning">
            {tCheckout.guards.cartEmpty}
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              component={Link}
              to="/"
              sx={{
                background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)'
              }}
            >
              {tCheckout.guards.startShopping}
            </Button>
          </Box>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      overflowY: 'scroll',
      scrollbarGutter: 'stable'
    }}>
      <Header />
      <Container maxWidth="xl" sx={{ my: 6, px: { xs: 2, sm: 3, md: 4 }, flex: 1 }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            {tCheckout.header.title}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            {tCheckout.header.subtitle}
          </Typography>
        </Box>
        
        {/* Progress Steps */}
        <StyledPaper sx={{ p: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            {steps.map((step, index) => (
              <React.Fragment key={step}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: index <= activeStep 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '1.2rem',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                      transition: 'all 0.3s ease',
                      mb: 2
                    }}
                  >
                    {index < activeStep ? <CheckCircleIcon /> : convertEnglishToBengali((index + 1).toString())}
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: index === activeStep ? 700 : 500,
                      color: index <= activeStep ? 'primary.main' : 'text.secondary',
                      textAlign: 'center'
                    }}
                  >
                    {step}
                  </Typography>
                </Box>
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      flex: 0.5,
                      height: 2,
                      background: index < activeStep 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : '#e0e0e0',
                      mx: 2,
                      borderRadius: 1,
                      transition: 'all 0.3s ease'
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={(activeStep / (steps.length - 1)) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              background: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 4
              }
            }}
          />
        </StyledPaper>

        {/* Main Content */}
        <Grid container spacing={4}>
          <Grid item xs={12} lg={8}>
            <Fade in timeout={500}>
              <Box>{renderStepContent(activeStep)}</Box>
            </Fade>
          </Grid>
          
          {/* Order Summary Sidebar */}
          {activeStep < 3 && (
            <Grid item xs={12} lg={4}>
              <OrderSummaryCard sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CartIcon sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {tCheckout.sidebar.orderSummary}
                  </Typography>
                </Box>
                
                <List sx={{ mb: 3 }}>
                  {cartItems.slice(0, 3).map((item, index) => (
                    <ListItem key={item.id} sx={{ px: 0, py: 2 }}>
                      <ListItemAvatar>
                        <Avatar
                          src={item.image}
                          alt={item.name}
                          variant="rounded"
                          sx={{ width: 50, height: 50, mr: 2 }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {item.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {convertEnglishToBengali(item.quantity.toString())} × {formatPriceWithBengaliDigits(item.price)}
                          </Typography>
                        }
                      />
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                        {formatPriceWithBengaliDigits(parsePrice(item.price) * item.quantity)}
                      </Typography>
                    </ListItem>
                  ))}
                  {cartItems.length > 3 && (
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {currentLanguage === 'en' ? `+${cartItems.length - 3} more items...` : `আরও ${convertEnglishToBengali((cartItems.length - 3).toString())}টি পণ্য...`}
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}
                </List>
                
                <Divider sx={{ my: 3, borderColor: 'primary.light' }} />
                
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">{tCheckout.order.subtotal}:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatPriceWithBengaliDigits(subtotal)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body1">{tCheckout.order.delivery}:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: deliveryFee === 0 ? 'success.main' : 'text.primary' }}>
                      {deliveryFee === 0 ? (currentLanguage === 'en' ? 'Free' : 'ফ্রি') : formatPriceWithBengaliDigits(deliveryFee)}
                    </Typography>
                  </Box>
                  {paymentFee > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">{tCheckout.payment.paymentFee}:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatPriceWithBengaliDigits(paymentFee)}
                      </Typography>
                    </Box>
                  )}
                </Stack>
                
                <Divider sx={{ my: 3, borderColor: 'primary.main' }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{tCheckout.order.total}:</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
                    {formatPriceWithBengaliDigits(total)}
                  </Typography>
                </Box>
                
                {deliveryFee === 0 && (
                  <Alert 
                    severity="success" 
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiAlert-icon': { fontSize: 24 }
                    }}
                  >
                    {currentLanguage === 'en' ? '🎉 You got free delivery!' : '🎉 ফ্রি ডেলিভারি পেয়েছেন!'}
                  </Alert>
                )}
                
                <Box sx={{ mt: 3, p: 2, background: 'rgba(102, 126, 234, 0.1)', borderRadius: 2 }}>
                  <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SecurityIcon color="success" fontSize="small" />
                    <Typography variant="caption" sx={{ ml: 0.5 }}>{tCheckout.sidebar.notices.securePayment}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ShippingIcon color="primary" fontSize="small" />
                    <Typography variant="caption" sx={{ ml: 0.5 }}>{tCheckout.sidebar.notices.fastDelivery}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <VerifiedIcon color="info" fontSize="small" />
                    <Typography variant="caption" sx={{ ml: 0.5 }}>{tCheckout.sidebar.notices.warranty}</Typography>
                    </Box>
                  </Stack>
                </Box>
              </OrderSummaryCard>
            </Grid>
          )}
        </Grid>
      </Container>
      
      <Footer />
      
      {/* Enhanced Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%', 
            borderRadius: 2,
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Checkout; 